import React from 'react'
import { ScoutIcon } from 'components/icons/scout'
import { PaperPlaneIcon } from 'components/icons/paper-plane'
import { v4 as uuid } from 'uuid'
import { streamTemplateChatResponse, TemplateChatResponse } from './data'
import './_styles.scss'
import '../content-pool/suggested/_styles.scss'

interface TemplateChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: Date
  attachments?: AttachedFile[]
}

interface QuickAction {
  prompt: string
  type: 'direct' | 'upload'
  message?: string // Optional custom message for upload type
}

interface TemplateChatProps {
  communityId: string
  templateName: string
  templateHtml: string
  documentation: any
  onTemplateUpdate: (updatedHtml: string) => void
  onError: (error: string) => void
}

interface AttachedFile {
  name: string
  type: string
  size: number
  dataUrl: string // base64 data URL
  preview?: string // preview URL for images
}

interface TemplateChatState {
  messages: TemplateChatMessage[]
  inputMessage: string
  isLoading: boolean
  isTyping: boolean
  sessionId: string
  showWelcome: boolean
  fileId: string | null // Track file_id for template persistence
  attachedFiles: AttachedFile[]
  autoSendOnAttach: boolean // Auto-send when file is attached (for quick actions)
}

export class TemplateChat extends React.Component<TemplateChatProps, TemplateChatState> {
  private chatEndRef: React.RefObject<HTMLDivElement>
  private chatInputRef: React.RefObject<HTMLInputElement>
  private fileInputRef: React.RefObject<HTMLInputElement>

  constructor(props: TemplateChatProps) {
    super(props)
    this.state = {
      messages: [],
      inputMessage: '',
      isLoading: false,
      isTyping: false,
      sessionId: uuid(),
      showWelcome: true,
      fileId: null, // Will be set after first template creation
      attachedFiles: [],
      autoSendOnAttach: false
    }
    this.chatEndRef = React.createRef()
    this.chatInputRef = React.createRef()
    this.fileInputRef = React.createRef()
  }

  componentDidMount() {
    this.scrollToBottom()
  }

  componentDidUpdate(prevProps: TemplateChatProps, prevState: TemplateChatState) {
    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom()
    }
  }

  private scrollToBottom = () => {
    setTimeout(() => {
      if (this.chatEndRef && this.chatEndRef.current) {
        this.chatEndRef.current.scrollTop = this.chatEndRef.current.scrollHeight
      }
    }, 100)
  }

  private handleSendMessage = () => {
    const message = this.state.inputMessage.trim()
    if (!message && this.state.attachedFiles.length === 0) return

    // Capture attachments BEFORE clearing state
    const attachmentsToSend = [...this.state.attachedFiles]

    // Add user message with attachments
    const userMessage: TemplateChatMessage = {
      role: 'user',
      content: message || '(Attached image)',
      timestamp: new Date(),
      attachments: attachmentsToSend.length > 0 ? attachmentsToSend : undefined
    }

    this.setState(
      {
        messages: [...this.state.messages, userMessage],
        inputMessage: '',
        isLoading: true,
        isTyping: true,
        showWelcome: false,
        attachedFiles: [] // Clear attachments after sending
      },
      () => {
        this.sendToBackend(message, attachmentsToSend)
      }
    )
  }

  private sendToBackend = (message: string, attachments: AttachedFile[]) => {
    const payload = {
      message,
      sessionId: this.state.sessionId,
      templateName: this.props.templateName,
      currentHtml: this.props.templateHtml, // Empty string if creating from scratch
      fileId: this.state.fileId, // Send existing file_id if we have one
      documentation: this.props.documentation,
      chatType: 'template-editing',
      attachments // Include attached files
    }

    let assistantMessage = ''
    let updatedHtml = ''

    const callback = (response: TemplateChatResponse | null) => {
      // Stream ended
      if (!response) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        return
      }

      // Handle error
      if (response.error) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        this.props.onError(response.error || 'Failed to communicate with Scout. Please try again.')
        return
      }

      // Handle message chunk
      if (response.message) {
        assistantMessage += response.message
        this.setState((prevState) => {
          const messages = [...prevState.messages]
          const lastMessage = messages[messages.length - 1]

          if (lastMessage && lastMessage.role === 'assistant') {
            messages[messages.length - 1] = {
              ...lastMessage,
              content: assistantMessage
            }
          } else {
            messages.push({
              role: 'assistant',
              content: assistantMessage,
              timestamp: new Date()
            })
          }

          return { messages, isTyping: false }
        })
      }

      // Handle HTML update and file_id
      if (response.updatedHtml) {
        updatedHtml = response.updatedHtml
        // Update immediately when HTML is received (don't wait for done)
        this.props.onTemplateUpdate(updatedHtml)
      }

      // Handle file_id (persist it for subsequent edits)
      if (response.fileId && !this.state.fileId) {
        this.setState({ fileId: response.fileId })
      }

      // Handle completion
      if (response.done) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
      }
    }

    streamTemplateChatResponse(this.props.communityId, payload, callback)
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      this.handleSendMessage()
    }
  }

  private handleAttachClick = (autoSend: boolean = false) => {
    // Set auto-send flag before opening file browser
    this.setState({ autoSendOnAttach: autoSend }, () => {
      if (this.fileInputRef.current) {
        this.fileInputRef.current.click()
      }
    })
  }

  private handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file type (images only for now)
    if (!file.type.startsWith('image/')) {
      this.props.onError('Please upload an image file (PNG, JPG, etc.)')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      this.props.onError('File size must be less than 10MB')
      return
    }

    try {
      // Convert to base64
      const dataUrl = await this.readFileAsDataURL(file)

      const attachedFile: AttachedFile = {
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl,
        preview: dataUrl // Use the same data URL for preview
      }

      // Check if we should auto-send
      const shouldAutoSend = this.state.autoSendOnAttach

      this.setState(prevState => ({
        attachedFiles: [...prevState.attachedFiles, attachedFile],
        autoSendOnAttach: false // Reset auto-send flag
      }), () => {
        // Auto-send if triggered by quick action
        if (shouldAutoSend) {
          this.handleSendMessage()
        }
      })
    } catch (error) {
      this.props.onError('Failed to read file')
    }

    // Reset input
    if (e.target) {
      e.target.value = ''
    }
  }

  private readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  private removeAttachment = (index: number) => {
    this.setState(prevState => ({
      attachedFiles: prevState.attachedFiles.filter((_, i) => i !== index)
    }))
  }

  private renderWelcome = () => {
    const hasExistingHtml = this.props.templateHtml && this.props.templateHtml.trim()

    const quickActions: QuickAction[] = !hasExistingHtml ? [
      {
        prompt: 'Create a newsletter template with header and 3 article sections',
        type: 'direct'
      },
      {
        prompt: 'Create a promotional email template',
        type: 'direct'
      },
      {
        prompt: 'Create a simple text-based announcement template',
        type: 'direct'
      },
      {
        prompt: 'Create a template from my design mockup 📎',
        type: 'upload',
        message: 'Create a template based on this design'
      }
    ] : [
      {
        prompt: 'Add a new article section',
        type: 'direct'
      },
      {
        prompt: 'Change header styling',
        type: 'direct'
      },
      {
        prompt: 'Modify footer content',
        type: 'direct'
      },
      {
        prompt: 'Update color scheme',
        type: 'direct'
      }
    ]

    return (
      <div className="rex-welcome-section">
        <div className="rex-welcome-message">
          <ScoutIcon svgwidth="40" svgheight="40"/>
          <div>
            <h2>Hi there! I'm Scout</h2>
            <p>
              {!hasExistingHtml
                ? 'I can help you create a brand new HTML template from scratch! Just describe what kind of template you need, and I\'ll build it for you. I understand Rasa\'s custom HTML documentation, template structure, and styling.'
                : 'I can help you modify your HTML template with ease. I understand Rasa\'s custom HTML documentation, template structure, and styling. What would you like to change today?'
              }
            </p>
          </div>
        </div>

        <div className="rex-quick-actions">
          <h3>{!hasExistingHtml ? 'Template Ideas' : 'Quick Actions'}</h3>
          <div className="rex-action-grid">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className={`rex-action-btn ${action.type === 'upload' ? 'upload' : ''}`}
                onClick={() => {
                  if (action.type === 'upload') {
                    // Upload type: set message and open file browser with auto-send
                    this.setState({ inputMessage: action.message || action.prompt }, () => {
                      this.handleAttachClick(true) // Pass true to enable auto-send
                    })
                  } else {
                    // Direct type: send message immediately
                    this.setState({ inputMessage: action.prompt }, () => {
                      this.handleSendMessage()
                    })
                  }
                }}
              >
                {action.prompt}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  private renderMessage = (message: TemplateChatMessage, index: number) => {
    return (
      <div
        key={index}
        className={`rex-message ${message.role === 'user' ? 'user' : 'assistant'}`}
      >
        <div className="rex-message-avatar">
          {message.role === 'user' ? (
            <i className="fas fa-user" />
          ) : (
            <ScoutIcon svgwidth="24" svgheight="24" />
          )}
        </div>
        <div className="rex-message-content">
          {message.attachments && message.attachments.length > 0 && (
            <div className="rex-message-attachments">
              {message.attachments.map((attachment, idx) => (
                <div key={idx} className="rex-message-attachment">
                  <img
                    src={attachment.preview || attachment.dataUrl}
                    alt={attachment.name}
                    className="rex-message-attachment-image"
                  />
                </div>
              ))}
            </div>
          )}
          <div className="rex-message-text">{message.content}</div>
          {message.timestamp && (
            <div className="rex-message-timestamp">
              {message.timestamp.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderTypingIndicator = () => {
    return (
      <div className="rex-message assistant">
        <div className="rex-message-avatar">
          <ScoutIcon svgwidth="24" svgheight="24" />
        </div>
        <div className="rex-message-content">
          <div className="rex-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="rex-main-content">
        <div className="rex-chat-container" ref={this.chatEndRef}>
          <div className="rex-messages">
            {this.state.showWelcome && this.state.messages.length === 0 && this.renderWelcome()}

            {this.state.messages.map((message, index) => this.renderMessage(message, index))}

            {this.state.isTyping && this.renderTypingIndicator()}
          </div>
        </div>

        <div className="rex-input-container">
          {/* Attachment Preview */}
          {this.state.attachedFiles.length > 0 && (
            <div className="rex-attachments-preview">
              {this.state.attachedFiles.map((file, index) => (
                <div key={index} className="rex-attachment-item">
                  <img src={file.preview} alt={file.name} className="rex-attachment-thumbnail" />
                  <div className="rex-attachment-info">
                    <span className="rex-attachment-name">{file.name}</span>
                    <span className="rex-attachment-size">{(file.size / 1024).toFixed(1)} KB</span>
                  </div>
                  <button
                    type="button"
                    className="rex-attachment-remove"
                    onClick={() => this.removeAttachment(index)}
                    title="Remove attachment"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <form className="rex-input-form" onSubmit={(e) => { e.preventDefault(); this.handleSendMessage(); }}>
            <div className="rex-input-wrapper">
              {/* Hidden file input */}
              <input
                ref={this.fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={this.handleFileSelect}
              />

              {/* Attach button */}
              <button
                type="button"
                className="rex-attach-btn"
                onClick={() => this.handleAttachClick()}
                disabled={this.state.isLoading}
                title="Attach image mockup"
              >
                📎
              </button>

              <input
                ref={this.chatInputRef}
                type="text"
                className="rex-message-input"
                placeholder="Ask Scout to modify your template or attach a mockup..."
                value={this.state.inputMessage}
                onChange={(e) => this.setState({ inputMessage: e.target.value })}
                onKeyDown={this.handleKeyDown}
                disabled={this.state.isLoading}
              />
              <button
                type="submit"
                className="rex-send-btn"
                disabled={(!this.state.inputMessage.trim() && this.state.attachedFiles.length === 0) || this.state.isLoading}
              >
                <PaperPlaneIcon />
              </button>
            </div>
          </form>
          <div className="rex-input-footer">
            <small>Scout is powered by AI and may make mistakes. Please verify important changes.</small>
          </div>
        </div>
      </div>
    )
  }
}
